/**
 * @coeldery/role-tab-bar —— **依角色嘅底部 Tab 導航欄**（P1.5；`decisions/015`）
 *
 * 為何有呢個模組（R1 查庫證據）：庫內 `@coeldery/bottom-tab-bar@1.0.0`（穩定）嘅
 * `TabId` union（`'family_tree' | …`）、label key 同 icon 都**綁死家庭樹產品**、
 * `scope = product:coeldery-family-tree` → 唔可以照用。SMS 要嘅係「**呼叫者傳入 tabs**、
 * 依登入角色唔同」→ 依 R3 新建 ＋ `derivedFrom` 留血緣（回流入庫）。
 *
 * 規格（同 `bottom-tab-bar` 一致，方便兩邊睇落一樣）
 *   · 固定底部 80px（含 `safe-area-inset-bottom`，iPhone 橫條唔會遮住）
 *   · 每個 tab：icon 28px ＋ 文字 18px Bold；active ＝ `--color-primary` ＋ **頂部 3px 指示線**
 *   · 熱區 ≥80px 高（整個 tab 可撳；手指友善）
 *   · 顏色一律 CSS var（**唔准 hardcode hex**）；文字一律走 i18n（`t(labelKey)`）
 *
 * ⚠️ 本模組**唔識路由**：`onSelect(id)` 由呼叫者決定去邊（SMS 用 react-router）。
 */
import { useTranslation } from 'react-i18next';
import { TabIconSvg, type TabIcon } from './icons.tsx';

/** 一個 tab 嘅定義（呼叫者砌；`to` 之類嘅路由資訊留喺 app 層） */
export interface TabDef {
  /** 穩定 id（app 層用嚟對路由） */
  id: string;
  /** i18n key（例 `tab.supplier.shipment`） */
  labelKey: string;
  /** 內置 icon 名（`icons.tsx` 嘅 `TabIcon`） */
  icon: TabIcon;
}

export interface RoleTabBarProps {
  /** 呢個角色嘅 tabs（**次序 ＝ 畫面次序**；一般 3–5 個） */
  tabs: readonly TabDef[];
  /** 而家 active 嘅 id */
  activeId: string;
  /** 撳 tab（呼叫者通常 `navigate(to)`） */
  onSelect: (id: string) => void;
}

const bar: React.CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  height: '80px',
  paddingBottom: 'env(safe-area-inset-bottom)',
  display: 'flex',
  alignItems: 'stretch',
  backgroundColor: 'var(--color-bg)',
  borderTop: '1px solid var(--color-divider)',
  zIndex: 20,
};

const tab: React.CSSProperties = {
  flex: 1,
  minHeight: '80px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: '6px 4px',
  position: 'relative',
  fontFamily: 'inherit',
};

export default function RoleTabBar({ tabs, activeId, onSelect }: RoleTabBarProps) {
  const { t } = useTranslation();

  return (
    <nav style={bar} aria-label={t('tab.aria')}>
      {tabs.map((tb) => {
        const active = tb.id === activeId;
        return (
          <button
            key={tb.id}
            type="button"
            onClick={() => onSelect(tb.id)}
            aria-current={active ? 'page' : undefined}
            style={tab}
          >
            {/* active 頂部 3px 指示線（＝ bottom-tab-bar 嘅視覺語言） */}
            {active && (
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '20%',
                  right: '20%',
                  height: '3px',
                  backgroundColor: 'var(--color-primary)',
                  borderRadius: '0 0 3px 3px',
                }}
              />
            )}
            <TabIconSvg name={tb.icon} active={active} />
            <span
              style={{
                fontSize: '18px',
                fontWeight: active ? 800 : 700,
                color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                lineHeight: 1.1,
              }}
            >
              {t(tb.labelKey)}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/** 內容區底部留白（唔好畀 tab bar 遮住最後一行） */
export const TAB_BAR_SPACER: React.CSSProperties = { height: '96px' };
